import { z } from 'zod';
import type { Permiso } from '../permisos';
import { dia, uuid } from './comunes';

export const filtrosReporteSchema = z
  .object({
    desde: dia,
    hasta: dia,
    barberoId: uuid.optional(),
    servicioId: uuid.optional(),
    agrupar: z.enum(['dia', 'semana', 'mes']).default('dia'),
    formato: z.enum(['json', 'xlsx', 'pdf']).default('json'),
  })
  .refine((f) => f.desde <= f.hasta, { message: 'La fecha inicial debe ser anterior a la final', path: ['hasta'] });
export type FiltrosReporte = z.infer<typeof filtrosReporteSchema>;

export type TipoColumna = 'texto' | 'dinero' | 'numero' | 'fecha' | 'fechaHora' | 'duracion' | 'porcentaje';

export interface ColumnaReporte {
  clave: string;
  titulo: string;
  tipo: TipoColumna;
  /** Clave de un campo de la fila: verdadero pinta la celda en verde, falso en rojo. */
  semaforo?: string;
}

export interface Reporte {
  clave: ClaveReporte;
  titulo: string;
  subtitulo: string;
  columnas: ColumnaReporte[];
  filas: Record<string, string | number | null>[];
  totales?: Record<string, string | number | null>;
  /** Datos para una gráfica sencilla (categoría → valor). */
  grafica?: { tipo: 'barras' | 'lineas'; etiqueta: string; valor: string; serie: string };
}

interface DefReporte {
  titulo: string;
  descripcion: string;
  grupo: 'Ventas' | 'Personal' | 'Caja' | 'Clientes' | 'Inventario';
  /** Basta con uno de estos permisos. */
  permisos: Permiso[];
  /** El barbero con `reportes.propios` solo ve lo suyo. */
  propio?: boolean;
  filtros: ('barbero' | 'servicio' | 'agrupar')[];
}

/** Catálogo de reportes (ERS §4.5 y §6.4). */
export const REPORTES = {
  'r1-pago-semanal': { titulo: 'Pago semanal por persona', descripcion: 'Sueldo ajustado por tiempo, comisiones por servicios y productos, propinas y faltantes.', grupo: 'Personal', permisos: ['reportes.dueno', 'reportes.propios'], propio: true, filtros: ['barbero'] },
  'r2-retrasos-faltas': { titulo: 'Retrasos, faltas y permisos', descripcion: 'Retardos, minutos tarde, faltas y permisos por trabajador.', grupo: 'Personal', permisos: ['reportes.dueno'], filtros: ['barbero'] },
  'r3-ingresos-periodo': { titulo: 'Ingresos por semana o mes', descripcion: 'Ventas cobradas agrupadas por periodo, separando servicios y productos.', grupo: 'Ventas', permisos: ['reportes.dueno', 'reportes.recepcion'], filtros: ['agrupar'] },
  'r4-gastos': { titulo: 'Gastos', descripcion: 'Sueldos, renta, compras y toda salida de dinero por categoría.', grupo: 'Caja', permisos: ['reportes.dueno'], filtros: [] },
  'r5-ventas-dia': { titulo: 'Ventas e ingresos', descripcion: 'Cada venta cobrada con su forma de pago, cambio y devoluciones.', grupo: 'Ventas', permisos: ['reportes.dueno', 'reportes.recepcion'], filtros: ['barbero'] },
  'r6-asistencia': { titulo: 'Asistencia detallada', descripcion: 'Llegada, comida, regreso y salida; tiempo extra, puntualidad y tiempo de menos.', grupo: 'Personal', permisos: ['reportes.dueno'], filtros: ['barbero'] },
  'r7-faltantes': { titulo: 'Faltantes de caja', descripcion: 'Faltantes indicando en el corte de quién se presentaron.', grupo: 'Caja', permisos: ['reportes.dueno'], filtros: [] },
  'r8-excedentes': { titulo: 'Excedentes de caja', descripcion: 'Excedentes indicando en el corte de quién se presentaron.', grupo: 'Caja', permisos: ['reportes.dueno'], filtros: [] },
  'r9-diferencias-totales': { titulo: 'Total de faltantes y excedentes', descripcion: 'Suma de faltantes y excedentes por responsable.', grupo: 'Caja', permisos: ['reportes.dueno'], filtros: [] },
  'r10-rendimiento': { titulo: 'Rendimiento del barbero', descripcion: 'Tiempo real contra estimado en los servicios e ingresos generados.', grupo: 'Personal', permisos: ['reportes.dueno'], filtros: ['barbero', 'servicio'] },
  'r11-comparativo': { titulo: 'Comparativo de barberos', descripcion: 'Quién fue el más rápido y quién obtuvo más comisiones.', grupo: 'Personal', permisos: ['reportes.dueno'], filtros: [] },
  'r12-devoluciones': { titulo: 'Devoluciones', descripcion: 'Devoluciones con motivo, monto y quién las autorizó.', grupo: 'Ventas', permisos: ['reportes.dueno'], filtros: [] },
  'r13-servicios-barbero': { titulo: 'Servicios y ventas por barbero', descripcion: 'A quién atendió cada barbero, qué servicio y cuánto tardó.', grupo: 'Ventas', permisos: ['reportes.dueno', 'reportes.recepcion', 'reportes.propios'], propio: true, filtros: ['barbero', 'servicio'] },
  'r14-cancelaciones': { titulo: 'Cancelaciones e inasistencias', descripcion: 'Citas canceladas y clientes que no se presentaron, con motivos.', grupo: 'Clientes', permisos: ['reportes.dueno', 'reportes.recepcion', 'reportes.propios'], propio: true, filtros: ['barbero'] },
  'r15-clientes': { titulo: 'Clientes nuevos y recurrentes', descripcion: 'Clientes atendidos por periodo y de dónde llegaron.', grupo: 'Clientes', permisos: ['reportes.dueno', 'reportes.recepcion'], filtros: [] },
  'r16-inventario': { titulo: 'Existencias y productos vendidos', descripcion: 'Stock actual, piezas vendidas y comisión por vendedor.', grupo: 'Inventario', permisos: ['reportes.dueno', 'reportes.recepcion'], filtros: ['barbero'] },
  'r17-entradas-salidas': { titulo: 'Entradas y salidas del mes', descripcion: 'Ingresos por cortes y productos contra compras, sueldos, renta e insumos.', grupo: 'Caja', permisos: ['reportes.dueno'], filtros: [] },
  'r18-cierres-caja': { titulo: 'Cierres de caja', descripcion: 'Quién cerró, efectivo esperado contra contado y todas las ventas del turno.', grupo: 'Caja', permisos: ['reportes.dueno'], filtros: [] },
  'r19-cortes-caja': { titulo: 'Cortes de caja', descripcion: 'Quién hizo el corte, efectivo esperado contra contado y lo cobrado en el turno hasta el corte.', grupo: 'Caja', permisos: ['reportes.dueno'], filtros: [] },
} as const satisfies Record<string, DefReporte>;

export type ClaveReporte = keyof typeof REPORTES;
export const CLAVES_REPORTE = Object.keys(REPORTES) as ClaveReporte[];
export const esClaveReporte = (v: string): v is ClaveReporte => Object.prototype.hasOwnProperty.call(REPORTES, v);

/** Detalle de un cierre o corte de caja: el arqueo y lo cobrado en su turno hasta ese momento. */
export interface ArqueoReporteDetalle {
  id: string;
  tipo: 'cierre' | 'corte';
  fecha: string;
  realizadoPor: string;
  turno: { id: string; responsable: string; abiertoEn: string; cerradoEn: string | null };
  fondoInicialMXN: number;
  esperadoMXN: number;
  contadoMXN: number;
  diferenciaMXN: number;
  esperadoUSD: number;
  contadoUSD: number;
  diferenciaUSD: number;
  ventas: {
    ventaId: string;
    folio: number;
    pagadaEn: string;
    cliente: string;
    barberos: string[];
    cobro: string;
    conceptos: { tipo: 'servicio' | 'producto'; descripcion: string; cantidad: number; totalMXN: number }[];
    totalMXN: number;
    pagos: { metodo: 'efectivo' | 'transferencia' | 'tarjeta'; moneda: 'MXN' | 'USD'; monto: number; montoMXN: number }[];
    pagadoMXN: number;
    cambioMXN: number;
    propinaMXN: number;
  }[];
  /** Fondo, retiros, gastos y ajustes del turno (el cambio ya va en cada venta). */
  movimientos: { tipo: string; concepto: string; moneda: 'MXN' | 'USD'; monto: number; fecha: string; usuario: string }[];
  totales: { ventasMXN: number; efectivoMXN: number; transferenciaMXN: number; tarjetaMXN: number; cambioMXN: number };
}
